/* eslint-disable no-restricted-globals */
import { FC, useState, useEffect } from 'react';
import html2canvas from 'html2canvas';
import cn from 'classnames';
import { FormControl, TextField } from '@mui/material';

import orderBy from 'lodash/orderBy';
import { toast } from 'react-toastify';
import Share from '../../../tour/regular/share/Share';
import { Props } from './types';
import {
  Root,
  Title,
  Icon,
  Result,
  ButtonGroup,
  Divider,
  Button,
  BackgroundFilter,
  IconAndText,
} from './styled';
import { StarRating } from './components/StarRating/StarRating';
import PublicAPI from '@/api/TreedisPublicDAO';
import { getVersion } from '@/components/util';
import { formatTime } from './service';
import { TopTable } from './components/TopTable/TopTable';

const emailPattern = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/;

export const FinishScreen: FC<Props & { children?: any }> = ({
  game,
  username,
  gamePoints,
  gameTime,
  tour,
  translation,
  sendRating,
  previousResult = false,
  isRtl,
}) => {
  const [showSharePopup, setShowSharePopup] = useState<boolean>(false);
  const [showSubmitRateBtn, setShowSubmitRateBtn] = useState<boolean>(!!sendRating);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [emails, setEmails] = useState('');
  const [top10, setTop10] = useState<any>([]);

  useEffect(() => {
    (async () => {
      const res = await PublicAPI.getExperienceTopScores(game.id);
      if (res.data) {
        const top = orderBy(
          res.data,
          [(item) => parseInt(item.point, 10), (item) => parseInt(item.time, 10)],
          ['desc', 'asc'],
        )
          .filter((item) => item.time > 0 && item.point > 0)
          .slice(0, 10)
          .map((item, index) => ({ ...item, index: index + 1, time: +item.time }));
        setTop10(top);
      }
    })();
  }, [game.id]);

  const sendEmail = async (emailArray: string[]) => {
    const commentMessageSection = `Comment left by participant after completing the virtual experience: ${comment}`;

    try {
      await PublicAPI.shareExperienceResult({
        data: {
          to: emailArray,
          params: {
            comment: comment ? commentMessageSection : null,
            participant: username,
            score: gamePoints,
            experience: game.name,
            userId: tour.userId,
            link: window.location.href,
          },
        },
      });
      toast.success('Emails sent successfully');
    } catch (err) {
      toast.error(`Failed to send emails`);
    }
  };

  const handleSubmitRating = async () => {
    if (emails.length > 0) {
      const emailArray = emails.split(',').map((email) => email.trim());
      const invalidEmails = emailArray.filter((email) => !emailPattern.test(email));

      if (invalidEmails.length > 0) {
        toast.error('Emails should be valid and separeted with coma');
        return;
      }
      await sendEmail(emailArray);
    }
    if (game?.finishScreenRateOn) {
      sendRating({ rating: rating || null, comment });
    }

    setShowSubmitRateBtn(false);
  };

  const handleDownload = () => {
    const screen: any = document.getElementsByClassName('game-endgame-screen');
    if (screen[0]) {
      html2canvas(screen[0], { useCORS: true }).then((canvas) => {
        // here is the most important part because if you dont replace you will get a DOM 18 exception.
        // const image = canvas.toDataURL("image/png").replace("image/png", "image/octet-stream");
        // window.location.href=image; // it will save locally
        // document.body.appendChild(canvas);
        const link = document.createElement('a');
        link.setAttribute('download', 'ExperienceResult.png');
        link.setAttribute(
          'href',
          canvas.toDataURL('image/png').replace('image/png', 'image/octet-stream'),
        );
        link.click();
      });
    }
  };
  const handleShare = () => {
    setShowSharePopup(true);
  };

  const backgroundImagePath = tour.introScreen?.backgroundImage
    ? `${process.env.NEXT_PUBLIC_CDN}/tours/${tour.id}/images/${
        tour.introScreen?.backgroundImage
      }${getVersion(tour.updatedAt)}`
    : `${process.env.NEXT_PUBLIC_CDN}/tours/${tour.id}/images/${tour.mainImage}${getVersion(
        tour.updatedAt,
      )}`;

  const shareLink = `${location.origin}/experience/${game.id}/${tour.slug}`;

  const finishScreenImage = game.finishScreenImage
    ? `${process.env.NEXT_PUBLIC_CDN}/tours/${tour.id}/game/${game.id}/${game.finishScreenImage}`
    : null;
  return (
    <Root backgroundImage={backgroundImagePath}>
      <BackgroundFilter rtl={isRtl}>
        <div className="col-md-6 offset-md-3 pt-5">
          <Icon finishScreenImage={finishScreenImage} />
          <Title>{game.finishScreenTitle || translation.congratulations}</Title>
          <Result>
            <IconAndText
              show={!!username}
              src="/experience/finishScreen/player.svg"
              text={username}
            />
            <div className="mr-2" />
            <IconAndText
              show={game.progressOn}
              src="/experience/finishScreen/score.svg"
              text={`${gamePoints}`}
            />
            <div className="mr-2" />
            <IconAndText
              show={game.timerOn}
              src="/experience/finishScreen/time.svg"
              text={formatTime(gameTime)}
            />
          </Result>
          <ButtonGroup show={game.finishScreenShareOn && !previousResult}>
            <Button show={game.finishScreenShareOn} onClick={handleShare}>
              <IconAndText
                show
                src="/experience/finishScreen/share.svg"
                text={translation.shareExperienceTour}
              />
            </Button>

            <Button onClick={handleDownload}>
              <i className="fas fa-download" />
              <p>{translation.downloadExperienceResult}</p>
            </Button>
          </ButtonGroup>

          <>
            <Divider />
            <FormControl className={cn('mb20')} fullWidth>
              {game.finishScreenRateOn && !previousResult && (
                <TextField
                  disabled={!showSubmitRateBtn}
                  multiline
                  rows={2}
                  variant="outlined"
                  label={translation?.comment || 'Submit'}
                  value={comment}
                  onChange={(ev) => setComment(ev?.target?.value)}
                  inputProps={{
                    style: { color: 'white' },
                  }}
                />
              )}
              {game.shareResult && (
                <TextField
                  disabled={!showSubmitRateBtn}
                  type="email"
                  rows={2}
                  variant="outlined"
                  label="Enter the email address to share your result"
                  value={emails}
                  onChange={(ev) => setEmails(ev?.target?.value)}
                  inputProps={{
                    style: { color: 'white', height: 'auto' },
                  }}
                  sx={{ flex: 1, marginTop: '20px' }}
                />
              )}
            </FormControl>
            {game.finishScreenRateOn && !previousResult && (
              <div className="d-flex justify-content-center align-items-center my-4">
                <p>{translation?.howWasExperience || 'How was your experience?'}</p>
                <p className="mr-2" />
                <StarRating canEdit={showSubmitRateBtn} onChange={(value) => setRating(value)} />
                <p className="mr-2" />
                <Button show={showSubmitRateBtn} onClick={handleSubmitRating}>
                  <p>{translation?.submit || 'Submit'}</p>
                </Button>
              </div>
            )}
            {!game.finishScreenRateOn && game.shareResult && (
              <div style={{ marginTop: 20, display: 'flex', justifyContent: 'center' }}>
                <Button show={showSubmitRateBtn} onClick={handleSubmitRating}>
                  <p>{translation?.submit || 'Submit'}</p>
                </Button>
              </div>
            )}
          </>

          {game.finishScreenLeaderboardOn && <TopTable data={top10} translation={translation} />}
          {showSharePopup && (
            <Share
              onClose={() => setShowSharePopup(false)}
              tour={tour}
              link={shareLink}
              hideShareLocation
              translation={translation}
            />
          )}
        </div>
      </BackgroundFilter>
    </Root>
  );
};
